import type { Content } from '@messenger/types'
import type { EditorTextBoxInput } from '@messenger/lotus/row-store'
import { GRACE_DISTANCE } from '@/utils/editor/textBoxLoadHeight'
import { asNumber } from './editorTextBoxSave'
import { editorPageJsonItem, editorTextBoxItemStub } from './editorTextBoxItems'
import { editorTextBoxConversionForCaseServices } from './editorTextBoxConversionServiceBindings'
import {
  EditorTextBoxConversionError,
  editorTextBoxFromTinyMceItem,
  type EditorTextBoxConversion,
  type EditorTextBoxConversionErrorReason,
  type EditorTextBoxConversionReportEntry,
  type EditorTextBoxTinyMceItem,
} from './tinymce-conversion'
import { editorTextBoxConversionReportCollector } from './tinymce-conversion/report'

/**
 * The height a heightless item's box takes: `painted`, the frame the canvas
 * painted for the page on screen, used as is; `measured`, the item's stored
 * HTML laid out off screen for any other page, to which the grace TinyMCE
 * allowed is added, because a Lotus frame shrinks text that runs more than a
 * pixel past it.
 */
export type EditorPageSetTurnoverHeight = {
  height: number
  source: 'painted' | 'measured'
}

export type EditorPageSetTurnoverInput = {
  /** Every page of the set, in order, as the editor holds them. */
  pages: readonly Content[]
  /** A height per heightless item id. */
  heights: Readonly<Record<string, EditorPageSetTurnoverHeight>>
  /** The service rows the case holds; a service block bound elsewhere converts unbound. */
  caseServiceIds: ReadonlySet<string>
  /** This run's fresh id, written to every page. */
  runId: string
}

/** One page's write: its JSON with converted items stubbed and this run's id set, and its new boxes. */
export type EditorPageSetTurnoverPage = {
  /** The page's `product.orderPageId`; null when the page JSON holds none. */
  pageId: string | null
  /** The page's 1-based place in the set. */
  pageNumber: number
  content: Content
  boxes: EditorTextBoxInput[]
}

/** A TinyMCE item the run left as it is, and why. */
export type EditorPageSetTurnoverSkip = {
  itemId: string | null
  pageId: string | null
  pageNumber: number
  reason: EditorTextBoxConversionErrorReason
}

/**
 * One entry of a turnover's report: the skipped boxes as one entry naming
 * every page they sit on, every frozen field of every box as one counted
 * entry with the keys it froze, or a loss the converter reports, merged
 * across boxes.
 */
export type EditorPageSetTurnoverReportEntry =
  | { kind: 'skipped'; count: number; pageNumbers: readonly number[] }
  | { kind: 'frozen-fields'; count: number; keys: readonly string[] }
  | Exclude<EditorTextBoxConversionReportEntry, { kind: 'frozen-field' }>

export type EditorPageSetTurnover = {
  pages: EditorPageSetTurnoverPage[]
  skipped: EditorPageSetTurnoverSkip[]
  convertedCount: number
  /** Skipped boxes first, then frozen fields, then other losses; empty when everything carried. */
  report: EditorPageSetTurnoverReportEntry[]
}

/** How many TinyMCE boxes, on which pages. */
export type EditorPageSetTurnoverTally = {
  count: number
  pageNumbers: number[]
}

export type EditorPageSetTurnoverCount = {
  converting: EditorPageSetTurnoverTally
  leaving: EditorPageSetTurnoverTally
}

type PageItem = Record<string, unknown>

const TINYMCE_ITEM_TYPE = 'text'

const isTinyMceItem = (item: unknown): item is PageItem =>
  typeof item === 'object' &&
  item !== null &&
  (item as PageItem).type === TINYMCE_ITEM_TYPE

const itemsOf = (page: Content): readonly unknown[] =>
  Array.isArray(page.product?.items) ? page.product.items : []

/** A page's `product.orderPageId`; null when there is no page or its JSON holds none. */
export const editorPageSetPageId = (
  page: Content | undefined,
): string | null => {
  const id = (page?.product as { orderPageId?: unknown } | undefined)
    ?.orderPageId
  return typeof id === 'string' && id !== '' ? id : null
}

const itemIdOf = (item: PageItem): string | null =>
  typeof item.id === 'string' && item.id !== '' ? item.id : null

/** No stored height, or a stored height of 0, which the converter would write as a 0-high box. */
const isHeightless = (item: PageItem): boolean => {
  const height = asNumber(item.height)
  return height === null || height === 0
}

/** A heightless TinyMCE item of a page: its id, its stored HTML (null when it holds none) and the width the converter reads. */
export type EditorPageSetHeightlessItem = {
  id: string
  html: string | null
  width: number
}

/**
 * The page's TinyMCE text items with an id that are heightless by the
 * converter's reading, the items a run needs a height supplied for.
 */
export const editorPageSetHeightlessItems = (
  page: Content,
): EditorPageSetHeightlessItem[] =>
  itemsOf(page).flatMap((item) => {
    if (!isTinyMceItem(item) || !isHeightless(item)) return []
    const id = itemIdOf(item)
    if (id === null) return []
    const html = (item.text as { value?: unknown } | undefined)?.value
    return [
      {
        id,
        html: typeof html === 'string' ? html : null,
        width: asNumber(item.width) ?? 0,
      },
    ]
  })

/** The height a heightless item converts at, or null when none was supplied. */
const suppliedHeight = (
  supplied: EditorPageSetTurnoverHeight | undefined,
): number | null => {
  if (
    supplied === undefined ||
    !Number.isFinite(supplied.height) ||
    supplied.height <= 0
  )
    return null
  return supplied.source === 'measured'
    ? supplied.height + GRACE_DISTANCE
    : supplied.height
}

type ItemOutcome =
  | { converted: EditorTextBoxConversion }
  | { refused: EditorTextBoxConversionErrorReason }

/** The converter's answer for one item; any error but its refusal is a defect and is raised. */
const convertItem = (item: PageItem, height: number | null): ItemOutcome => {
  if (isHeightless(item) && height === null) return { refused: 'height' }
  const input = (
    isHeightless(item) ? { ...item, height } : item
  ) as EditorTextBoxTinyMceItem
  try {
    return { converted: editorTextBoxFromTinyMceItem(input) }
  } catch (error) {
    if (error instanceof EditorTextBoxConversionError)
      return { refused: error.reason }
    throw error
  }
}

const distinct = <T>(values: readonly T[]): T[] => [...new Set(values)]

const mergedReport = (
  skipped: readonly EditorPageSetTurnoverSkip[],
  entries: readonly EditorTextBoxConversionReportEntry[],
): EditorPageSetTurnoverReportEntry[] => {
  const report: EditorPageSetTurnoverReportEntry[] = []
  if (skipped.length > 0)
    report.push({
      kind: 'skipped',
      count: skipped.length,
      pageNumbers: distinct(skipped.map((skip) => skip.pageNumber)),
    })
  const frozen = entries.filter(
    (entry): entry is Extract<typeof entry, { kind: 'frozen-field' }> =>
      entry.kind === 'frozen-field',
  )
  if (frozen.length > 0)
    report.push({
      kind: 'frozen-fields',
      count: frozen.reduce((sum, entry) => sum + entry.count, 0),
      keys: distinct(frozen.map((entry) => entry.key)),
    })
  const others = editorTextBoxConversionReportCollector()
  for (const entry of entries)
    if (entry.kind !== 'frozen-field') others.add(entry)
  for (const entry of others.entries())
    if (entry.kind !== 'frozen-field') report.push(entry)
  return report
}

/**
 * Every TinyMCE text item of a page set as a Lotus box under the item's own
 * id, under the one-box conversion's rules, with the case-service step after
 * it. A heightless item converts at the height supplied for it; one with no
 * supplied height, and any item the converter refuses, stays a TinyMCE item
 * and is listed with its reason and page. Every page, one with nothing to
 * convert included, takes this run's id in place of an earlier run's. Pure:
 * the pages passed in are not changed.
 */
export const editorPageSetTurnover = ({
  pages,
  heights,
  caseServiceIds,
  runId,
}: EditorPageSetTurnoverInput): EditorPageSetTurnover => {
  const skipped: EditorPageSetTurnoverSkip[] = []
  const entries: EditorTextBoxConversionReportEntry[] = []
  let convertedCount = 0
  const written = pages.map((page, index): EditorPageSetTurnoverPage => {
    const pageId = editorPageSetPageId(page)
    const pageNumber = index + 1
    const boxes: EditorTextBoxInput[] = []
    const items = itemsOf(page).map((item) => {
      if (!isTinyMceItem(item))
        return editorPageJsonItem(item as { id?: string; type?: string })
      const itemId = itemIdOf(item)
      const outcome = convertItem(
        item,
        suppliedHeight(itemId === null ? undefined : heights[itemId]),
      )
      if ('refused' in outcome) {
        skipped.push({ itemId, pageId, pageNumber, reason: outcome.refused })
        return item
      }
      const conversion = editorTextBoxConversionForCaseServices(
        outcome.converted,
        caseServiceIds,
      )
      boxes.push(conversion.box)
      entries.push(...conversion.report)
      convertedCount += 1
      return editorTextBoxItemStub(conversion.box.boxId)
    })
    return {
      pageId,
      pageNumber,
      boxes,
      content: {
        ...page,
        product: {
          ...page.product,
          items: items as Content['product']['items'],
        },
        pageProperties: { ...page.pageProperties, lotusTurnoverRunId: runId },
      },
    }
  })
  return {
    pages: written,
    skipped,
    convertedCount,
    report: mergedReport(skipped, entries),
  }
}

/**
 * Stands in for a heightless item's height while counting: the run supplies
 * its text's height, so only the converter's other refusals decide it.
 */
const COUNT_STAND_IN_HEIGHT = 1

const tallyOf = (
  pageNumbers: readonly number[],
): EditorPageSetTurnoverTally => ({
  count: pageNumbers.length,
  pageNumbers: distinct(pageNumbers),
})

/**
 * How many TinyMCE boxes a turnover of these pages would convert and would
 * leave, and on which pages, under the converter's refusals; nothing is
 * written. A heightless item counts as converting unless the converter
 * refuses it for another reason.
 */
export const editorPageSetTurnoverCount = (
  pages: readonly Content[],
): EditorPageSetTurnoverCount => {
  const converting: number[] = []
  const leaving: number[] = []
  pages.forEach((page, index) => {
    for (const item of itemsOf(page)) {
      if (!isTinyMceItem(item)) continue
      const outcome = convertItem(item, COUNT_STAND_IN_HEIGHT)
      ;('refused' in outcome ? leaving : converting).push(index + 1)
    }
  })
  return { converting: tallyOf(converting), leaving: tallyOf(leaving) }
}
